import React, { useState, useRef } from 'react'
import { Upload, FileText, X, AlertTriangle, CheckCircle } from 'lucide-react'
import { useScreening } from '../../context/ScreeningContext'
import GlowingButton from '../Common/GlowingButton'

export default function UploadStep({ onNext, onPrev, errors }) {
  const { 
    uploadedFiles, 
    uploadFiles,    // UTILISER uploadFiles
    removeFile,
    getCurrentSession,
    createSession
  } = useScreening()

  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return

    try {
      setUploading(true)
      console.log('📤 UploadStep - Upload de', files.length, 'fichiers vers Appwrite...')
      
      // S'assurer qu'on a une session
      let sessionId = getCurrentSession()
      if (!sessionId) {
        console.log('🆕 Création nouvelle session...')
        sessionId = await createSession()
      }
      
      // Utiliser uploadFiles du context
      const result = await uploadFiles(files)
      
      console.log('✅ UploadStep - Upload terminé:', result.successCount, 'succès')
      
      if (result.errorCount > 0) {
        alert(`Upload terminé avec ${result.errorCount} erreurs. Vérifiez la console.`)
      }
      
    } catch (error) {
      console.error('❌ UploadStep - Erreur upload:', error)
      alert(`Erreur upload: ${error.message}`)
    } finally {
      setUploading(false)
    }
  }

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    handleFileUpload(files)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    
    const files = Array.from(e.dataTransfer.files)
    const pdfFiles = files.filter(file => file.type === 'application/pdf')
    
    if (pdfFiles.length !== files.length) {
      alert('Seuls les fichiers PDF sont acceptés')
    }
    
    if (pdfFiles.length > 0) {
      handleFileUpload(pdfFiles)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragActive(false)
  }

  const handleRemoveFile = async (fileId) => {
    try {
      await removeFile(fileId)
    } catch (error) {
      console.error('❌ Erreur suppression:', error)
      alert(`Erreur suppression: ${error.message}`)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="space-y-8 h-full flex flex-col">
      <div className="text-center">
        <div className="relative inline-block mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-3xl blur-lg opacity-50 animate-pulse"></div>
          <div className="relative w-20 h-20 bg-gradient-to-r from-pink-500 to-blue-500 rounded-3xl flex items-center justify-center shadow-2xl">
            <span className="text-3xl font-bold text-white">1</span>
          </div>
        </div>
        <h2 className="text-3xl font-bold text-white mb-4">Upload des CV</h2>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
          Glissez-déposez vos fichiers PDF ou cliquez pour les sélectionner
        </p>
      </div>

      {/* Contenu principal avec scroll contrôlé */}
      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full min-h-0">
        
        {/* Zone de drop - FIXE */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 flex-shrink-0 ${
            dragActive
              ? 'border-blue-400 bg-blue-400/10 scale-105'
              : uploadedFiles.length > 0
              ? 'border-green-500 bg-green-500/5'
              : 'border-gray-600 bg-white/5 hover:border-gray-400 hover:bg-white/10'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="space-y-4">
            <div className="text-6xl">
              {uploading ? (
                <div className="animate-spin text-blue-400">⏳</div>
              ) : uploadedFiles.length > 0 ? (
                <div className="text-green-400">✅</div>
              ) : (
                <Upload className="w-16 h-16 mx-auto text-gray-400" />
              )}
            </div>
            
            <div>
              <h3 className="text-xl font-bold text-white mb-2">
                {uploading
                  ? 'Upload en cours...'
                  : uploadedFiles.length > 0
                  ? `${uploadedFiles.length} fichier(s) uploadé(s)`
                  : 'Glissez vos CV ici'
                }
              </h3>
              <p className="text-gray-400">
                {uploading
                  ? 'Veuillez patienter pendant l\'upload'
                  : 'Formats acceptés: PDF • Taille max: 10MB par fichier'
                }
              </p>
            </div>

            {!uploading && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <GlowingButton
                  onClick={() => fileInputRef.current?.click()}
                  className="mx-auto"
                  disabled={uploading}
                >
                  {uploadedFiles.length > 0 ? '📎 Ajouter d\'autres CV' : '📁 Sélectionner des fichiers'}
                </GlowingButton>
              </div>
            )}
          </div>
        </div>

        {/* Liste des fichiers uploadés avec SCROLL */}
        {uploadedFiles.length > 0 && (
          <div className="mt-8 flex-1 flex flex-col min-h-0">
            <h4 className="text-xl font-bold text-white flex items-center mb-4 flex-shrink-0">
              <FileText className="w-6 h-6 mr-3 text-blue-400" />
              Fichiers uploadés ({uploadedFiles.length})
            </h4>
            
            {/* CONTENEUR AVEC SCROLL */}
            <div className="flex-1 overflow-y-auto pr-2 space-y-3 max-h-96">
              {uploadedFiles.map((file, index) => (
                <div key={file.id} className="glass-card p-4 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h5 className="text-white font-medium truncate max-w-xs">{file.file_name}</h5>
                        <p className="text-gray-400 text-sm">
                          {formatFileSize(file.file_size)} • Uploadé
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3 flex-shrink-0">
                      <div className="text-green-400">
                        <CheckCircle className="w-5 h-5" />
                      </div>
                      <button
                        onClick={() => handleRemoveFile(file.id)}
                        className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Indicateur de scroll si beaucoup de fichiers */}
              {uploadedFiles.length > 6 && (
                <div className="text-center py-2 text-gray-500 text-sm">
                  ↕️ Faites défiler pour voir plus de fichiers
                </div>
              )}
            </div>
          </div>
        )}

        {/* Erreurs - FIXE */}
        {errors.files && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex-shrink-0">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-400 mr-3" />
              <span className="text-red-300">{errors.files}</span>
            </div>
          </div>
        )}

        {/* Informations utiles - FIXE */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg flex-shrink-0">
          <h6 className="text-blue-300 font-medium mb-2">💡 Conseils d'upload</h6>
          <ul className="text-blue-200 text-sm space-y-1">
            <li>• Les fichiers sont stockés de manière sécurisée dans Appwrite</li>
            <li>• Utilisez des noms de fichiers explicites (ex: CV_Jean_Dupont.pdf)</li>
            <li>• Les CV sont automatiquement sauvegardés pour vos analyses futures</li>
            <li>• Vous pouvez ajouter des fichiers à tout moment</li>
          </ul>
        </div>
      </div>

      {/* Boutons de navigation - TOUJOURS VISIBLES */}
      <div className="flex justify-center space-x-4 pt-8 flex-shrink-0 border-t border-gray-700/50 bg-gray-900/50 backdrop-blur-sm rounded-t-xl">
        {onPrev && (
          <GlowingButton variant="secondary" onClick={onPrev}>
            ← Retour
          </GlowingButton>
        )}
        <GlowingButton 
          onClick={onNext}
          disabled={uploadedFiles.length === 0 || uploading}
        >
          {uploadedFiles.length === 0 
            ? 'Uploadez au moins un CV' 
            : `Continuer avec ${uploadedFiles.length} CV →`
          }
        </GlowingButton>
      </div>

      {/* Style pour la scrollbar personnalisée */}
      <style jsx>{`
        .overflow-y-auto::-webkit-scrollbar {
          width: 8px;
        }
        .overflow-y-auto::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.5);
          border-radius: 4px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.7);
        }
      `}</style>
    </div>
  )
}